import React, { useEffect, useRef, useState } from "react";
import { Switch, Grid, notification } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";

import {
  ContractInformationActionCreators,
  DownloadPDFContractActionsCreators,
  AutoReleaseActionsCreators,
  InquiryDetailActionsCreators,
} from "redux/rootActions";
import { contractInformationDataResponse } from "redux/contract/information/contractInformationStates";
import { downloadPDFContractDataResponse } from "redux/contract/downloadPDF/downloadPDFStates";
import { autoReleaseDataResponse } from "redux/contract/autoRelease/autoReleaseStates";
import { inquiryDetailDataResponse } from "redux/contract/inquiryDetail/inquiryDetailStates";

import { Box, Button, Input, TextArea } from "components";
import {
  WrapperContractInformation,
  WrapperTable,
  Title,
  Row,
  WrapperGroupButton,
  WrapperAutoUnlinkSetting,
  WrapperPaymentInformation,
  WrapperRow,
  ModalStyled,
  WrapperSendMessageBtn,
  SwitchStyled,
  WrapperErrorMessage,
  ErrorMessage,
} from "./ContractInformation.style";
import { httpStatus } from "configs/httpStatus";
import { SendCompleteIcon } from "icons";
import { localStorageHelper } from "utils";
import moment from "moment";

const { useBreakpoint } = Grid;

const ContractInformation = () => {
  const { t }: any = useTranslation("translation", {
    keyPrefix: "contract",
  });
  const { t: t1 }: any = useTranslation("translation", {
    keyPrefix: "account",
  });
  const { t: t2 }: any = useTranslation("translation", {
    keyPrefix: "setting",
  });
  const { t: t3 }: any = useTranslation("translation", {
    keyPrefix: "message-delivery",
  });
  const { t: t4 }: any = useTranslation("translation", {
    keyPrefix: "user-list",
  });

  const screens = useBreakpoint();
  const dispatch = useDispatch();
  const currentLanguage = localStorageHelper.getItem("i18nextLng");
  const contractInformationData = useSelector(contractInformationDataResponse);
  const downloadPDFContractData = useSelector(downloadPDFContractDataResponse);
  const autoReleaseData = useSelector(autoReleaseDataResponse);
  const inquiryDetailData = useSelector(inquiryDetailDataResponse);

  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState("");
  const [linkReleaseData, setLinkReleaseData] = useState({
    autoRelease: false,
    day: "0",
  });

  const { watch, handleSubmit, control } = useForm();

  const replyEmail = watch("reply-email");
  const detail = watch("detail");

  useEffect(() => {
    dispatch(ContractInformationActionCreators.contractInformationAction());
  }, []);

  useEffect(() => {
    if (
      downloadPDFContractData &&
      Object.keys(downloadPDFContractData).length
    ) {
      window.open(downloadPDFContractData.result.pdf_url);
      dispatch(DownloadPDFContractActionsCreators.handleResetAction());
    }
  }, [downloadPDFContractData]);

  useEffect(() => {
    if (
      contractInformationData &&
      Object.keys(contractInformationData).length
    ) {
      const { auto_release, days_until_auto_release } =
        contractInformationData.result;
      setLinkReleaseData({
        autoRelease: auto_release,
        day: days_until_auto_release,
      });
    }
  }, [contractInformationData]);

  useEffect(() => {
    if (autoReleaseData && Object.keys(autoReleaseData).length) {
      if (autoReleaseData.status === httpStatus.StatusOK) {
        notification.success({
          message: "",
          description: t2("general-setting-apply"),
          placement: "bottomRight",
          icon: <SendCompleteIcon />,
        });
        dispatch(ContractInformationActionCreators.contractInformationAction());
        dispatch(AutoReleaseActionsCreators.handleResetAction());
      }
    }
  }, [autoReleaseData]);

  useEffect(() => {
    if (inquiryDetailData && Object.keys(inquiryDetailData).length) {
      if (inquiryDetailData.status === httpStatus.StatusOK) {
        notification.success({
          message: "",
          description: t2("general-setting-apply"),
          placement: "bottomRight",
          icon: <SendCompleteIcon />,
        });
        dispatch(InquiryDetailActionsCreators.handleResetAction());
      }
    }
  }, [inquiryDetailData]);

  const handleOnChangeSwitch = (value: any) => {
    setLinkReleaseData((prevState: any) => ({
      ...prevState,
      autoRelease: value,
    }));
    const params = {
      auto_release: value,
      days_until_auto_release: Number(linkReleaseData.day),
    };
    dispatch(AutoReleaseActionsCreators.autoReleaseAction(params));
  };

  const handleOnChangeDay = (e: any) => {
    if (Number(e.target.value) < 1 || Number(e.target.value) > 500) {
      setError("day-invalid");
    } else {
      setError("");
    }

    setLinkReleaseData((prevState: any) => ({
      ...prevState,
      day: e.target.value,
    }));
  };

  const handleSaveLinkRelease = () => {
    if (error) return;

    const params = {
      auto_release: linkReleaseData.autoRelease,
      days_until_auto_release: Number(linkReleaseData.day),
    };
    dispatch(AutoReleaseActionsCreators.autoReleaseAction(params));
  };

  const handleDownloadPDF = () => {
    dispatch(DownloadPDFContractActionsCreators.downloadPDFAction());
  };

  const onSubmitOtherInquiries = (data: any) => {
    const params = {
      inquiry_email: data.replyEmail,
      inquiry_desc: data.detail,
    };
    dispatch(InquiryDetailActionsCreators.sendInquiryDetailAction(params));
  };

  const getDate = (value: string | any) => {
    const format: any = {
      ja: "YYYY年MM月DD日",
      en: "YYYY-MM-DD",
      vi: "DD/MM/YYYY",
    };

    return moment(value).format(format[currentLanguage || "ja"]);
  };

  return (
    <Box
      title={
        currentLanguage === "vi"
          ? "Cài đặt & Thông tin hợp đồng"
          : "設定・契約情報"
      }
      padding="16px 16px"
    >
      <WrapperContractInformation>
        <WrapperTable>
          <div>
            <Title>{t("contract-information")}</Title>
            <Row>
              <div>{t("application-date")}</div>
              <div className="pd">
                {getDate(contractInformationData?.result?.contract_start_date)}
              </div>
            </Row>
            <Row>
              <div>{t("contractor")}</div>
              <div>
                <Row>
                  <div className="bg">{t("company-name")}</div>
                  <div className="pd">
                    {currentLanguage === "vi"
                      ? "Công ty LEBER"
                      : "株式会社リーバー"}
                  </div>
                </Row>
                <Row>
                  <div className="bg">{t("address")}</div>
                  <div className="pd">
                    {contractInformationData?.result?.address}
                  </div>
                </Row>
              </div>
            </Row>
            <Row>
              <div>{t("contact-information")}</div>
              <div>
                <Row>
                  <div className="bg">{t("address")}</div>
                  <div className="pd">
                    {contractInformationData?.result?.address}
                  </div>
                </Row>
                <Row>
                  <div className="bg">{t("company-representation")}</div>
                  <div className="pd">
                    {contractInformationData?.result?.company_representation}
                  </div>
                </Row>
                <Row>
                  <div className="bg">{t("tele-phone")}</div>
                  <div className="pd">
                    {contractInformationData?.result?.contact_number}
                  </div>
                </Row>
                <Row>
                  <div className="bg">{t1("email")}</div>
                  <div className="pd">
                    {contractInformationData?.result?.email}
                  </div>
                </Row>
              </div>
            </Row>
            <Row>
              <div>{t("contract-start-date")}</div>
              <div className="pd">
                {getDate(contractInformationData?.result?.contract_start_date)}
              </div>
            </Row>
            <Row>
              <div>{t("application-plan")} </div>
              <div className="pd">
                {contractInformationData?.result?.application_plan}
              </div>
            </Row>
            <Row>
              <div>{t("monthly-used-fee")}</div>
              <div className="pd">
                {contractInformationData?.result?.monthly_point}
              </div>
            </Row>
            <Row>
              <div>{t("bank-detail")}</div>
              <div className="pd">
                {contractInformationData?.result?.bank_detail}
              </div>
            </Row>
            <Row>
              <div>{t("remark")}</div>
              <div className="pd">
                {contractInformationData?.result?.remark}
              </div>
            </Row>
          </div>
        </WrapperTable>
        <WrapperGroupButton>
          {screens.md && (
            <Button
              name={t("contract-pdf-output")}
              background="#2AC769"
              color="#FFFFFF"
              border="none"
              fontSize={16}
              fontWeight={700}
              padding="8px 15px"
              onClick={handleDownloadPDF}
            />
          )}

          <Button
            name={t("change-plan")}
            background="#2AC769"
            color="#FFFFFF"
            border="none"
            fontSize={16}
            fontWeight={700}
            padding="8px 15px"
            onClick={() => {
              window.open(
                "https://docs.google.com/forms/d/1a71P60L4VRTsBbHy5xiwqqA4-Mb97TgNKPVEED73GMI/viewform?edit_requested=true"
              );
            }}
          />
          <Button
            name={t("other-inquiries")}
            background="#2AC769"
            color="#FFFFFF"
            border="none"
            fontSize={16}
            fontWeight={700}
            padding="8px 15px"
            onClick={() => setIsVisible(true)}
          />
        </WrapperGroupButton>
      </WrapperContractInformation>
      <WrapperAutoUnlinkSetting>
        <Title>{t("auto-unlink-setting")}</Title>
        <div className="form">
          <SwitchStyled
            onChange={handleOnChangeSwitch}
            checked={linkReleaseData.autoRelease}
            defaultChecked={false}
          />

          <div
            style={{
              pointerEvents: linkReleaseData.autoRelease ? "auto" : "none",
              opacity: linkReleaseData.autoRelease ? 1 : 0.25,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 5,
                position: "relative",
              }}
            >
              <Input
                width={65}
                height={31}
                onChange={handleOnChangeDay}
                value={linkReleaseData.day}
                // disabled={!autoRelease}
              />
              {error && (
                <WrapperErrorMessage>
                  <ErrorMessage>{t(error)}</ErrorMessage>
                </WrapperErrorMessage>
              )}

              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {currentLanguage === "vi"
                  ? "Sau bao nhiêu ngày sẽ tự động hủy"
                  : "日後に解除"}
              </div>
            </div>
            <div>{t("set-value")}</div>
          </div>
          <div
            style={{
              pointerEvents: linkReleaseData.autoRelease ? "auto" : "none",
              opacity: linkReleaseData.autoRelease ? 1 : 0.25,
            }}
          >
            <Button
              name={t2("keep")}
              background={linkReleaseData.autoRelease ? "#2AC769" : "#CCCCCC"}
              color="#FFFFFF"
              border="none"
              fontSize={16}
              fontWeight={700}
              padding="3px 16px"
              onClick={handleSaveLinkRelease}
              // disabled={!autoRelease}
            />
          </div>
        </div>
      </WrapperAutoUnlinkSetting>
      <WrapperPaymentInformation>
        <div>
          <Title>{t("payment-information")}</Title>
          <WrapperRow>
            <div>{t("target-month")}</div>
            <div>{t("debit-date")}</div>
            <div>{t("payment-status")}</div>
            <div>{t("amount-money")}</div>
            <div>{t("comment")}</div>
          </WrapperRow>

          {contractInformationData?.result?.payment_details.map((item: any) => {
            return (
              <WrapperRow key={item.id}>
                <div>
                  {moment(item.payment_month).format("MM")}
                  <span style={{ paddingLeft: 3 }}>{t("month")}</span>
                </div>
                <div>{getDate(item.withdrawal_date)}</div>
                <div>{item.status && t("completed")}</div>
                <div>
                  {item.withdrawal_amount}
                  <span style={{ paddingLeft: 5 }}>{t("yen")}</span>
                </div>
                <div>{item.comment}</div>
              </WrapperRow>
            );
          })}
        </div>
      </WrapperPaymentInformation>
      {isVisible && (
        <ModalStyled
          type="form"
          title={t("other-inquiries-2")}
          visible={isVisible}
          onCancel={() => {
            setIsVisible(false);
          }}
        >
          <form onSubmit={handleSubmit(onSubmitOtherInquiries)}>
            <Controller
              control={control}
              name="replyEmail"
              render={({ field: { onChange }, fieldState: { error } }) => (
                <Input
                  label={t("reply-email")}
                  height={54}
                  fw={700}
                  fsLabel={16}
                  fwLabel={700}
                  marginForm="0 0 16px"
                  lhLabel="22px"
                  bdr={12}
                  onChange={onChange}
                  error={t(error?.message)}
                />
              )}
            />
            <Controller
              control={control}
              name="detail"
              render={({ field: { onChange }, fieldState: { error } }) => (
                <TextArea
                  onChange={onChange}
                  error={t(error?.message)}
                  height="248px"
                  fsLabel={16}
                  fwLabel={700}
                  lhLabel="22px"
                  marginForm="0 0 16px"
                  label={t("inquiries-detail")}
                />
              )}
            />

            <WrapperSendMessageBtn>
              <Button
                background="#E0E0E0"
                color="#FFFFFF"
                name={t4("cancel")}
                border="none"
                bdr="6px"
                fontSize={16}
                fontWeight={700}
                lineHeight="16px"
                onClick={() => setIsVisible(false)}
              />
              <Button
                type="submit"
                background="#2AC769"
                color="#FFFFFF"
                name={t3("send")}
                border="none"
                bdr="6px"
                fontSize={16}
                fontWeight={700}
                lineHeight="23px"
                disabled={!replyEmail && !detail}
              />
            </WrapperSendMessageBtn>
          </form>
        </ModalStyled>
      )}
    </Box>
  );
};

export default ContractInformation;
